import { initializeApp } from "firebase/app";
import { 
  getFirestore, collection, addDoc, deleteDoc, updateDoc, doc, onSnapshot, query, orderBy, setDoc, getDoc, getDocs, deleteField, initializeFirestore, persistentLocalCache, persistentMultipleTabManager
} from "firebase/firestore"; 
import { 
  getAuth, signInWithEmailAndPassword, deleteUser, createUserWithEmailAndPassword, signOut, onAuthStateChanged 
} from "firebase/auth";
import React, { useContext, useState, useEffect } from "react";
import { firebaseConfig } from "./firebaseConfig";
import { getAnalytics, isSupported } from "firebase/analytics";

const app = initializeApp(firebaseConfig);

export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({tabManager: persistentMultipleTabManager()})
});
const auth = getAuth(app);

export let analytics;
isSupported().then((supported) => {
    if (supported) {
        analytics = getAnalytics(app);
    }
});

const DataContext = React.createContext();

export function useData() {
  return useContext(DataContext);
}

// Updated Defaults
const DEFAULT_NAV = [
    { title: "Home", link: "/", visible: true, type: 'system' },
    { title: "Full Schedule", link: "/schedule", visible: true, type: 'system' },
    { title: "Standings & Results", link: "/standings", visible: true, type: 'system' },
    { title: "Past Streams", link: "/archive", visible: true, type: 'system' },
    { title: "Conditions of Contest", link: "/rules", visible: true, type: 'system' },
    { title: "Donate", link: "/donate", visible: false, type: 'system' },
];

export function DataProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null); // NEW: Stores { role: 'admin'|'captain', teamId: '...', email: '...' }
    
    const [schedule, setSchedule] = useState([]);
    const [conditionsText, setConditionsText] = useState(null);
    const [teams, setTeams] = useState([]); 
    const [standings, setStandings] = useState([]);
    const [homeContent, setHomeContent] = useState({ welcomeText: "", welcomeVideo: "" });
    const [donationsContent, setDonationsContent] = useState({ text: "", link: "" });
    const [allLineups, setAllLineups] = useState({});
    const [aliasesCsv, setAliasesCsv] = useState("");
    
    const [navigation, setNavigation] = useState(DEFAULT_NAV);
    const [customPages, setCustomPages] = useState([]); 
    const [globalStyle, setGlobalStyle] = useState(null);
    const [allUsers, setAllUsers] = useState([]); // NEW: List of all users for Admin management

    // Loading States
    const [authReady, setAuthReady] = useState(false);
    const [navReady, setNavReady] = useState(false);
    const [brackets, setBrackets] = useState({ main: {}, consolation: {} });
    const loading = !authReady || !navReady;

    const [isVisible, setIsVisible] = useState(document.visibilityState === 'visible');

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsVisible(document.visibilityState === 'visible');
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // 1. Auth & Role Listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                // Fetch Role
                try {
                    const rolesDoc = await getDoc(doc(db, "system", "roles"));
                    if (rolesDoc.exists() && rolesDoc.data()[currentUser.uid]) {
                        // Extract just this user's data from the master list
                        setUserRole(rolesDoc.data()[currentUser.uid]);
                    } else {
                        // Fallback for legacy admins
                        setUserRole({ role: 'admin' }); 
                    }
                } catch (err) {
                    console.warn("Offline or missing cache: Could not fetch user role.", err);
                    setUserRole(null); // Safely default to standard user if offline
                }
            } else {
                setUser(null);
                setUserRole(null);
            }
            setAuthReady(true);
        });
        return unsubscribe;
    }, []);

    // 2. Schedule Listener
    useEffect(() => {
        if (!isVisible) return;

        // We now listen to ONE document!
        const unsubscribe = onSnapshot(doc(db, "system", "schedule"), (docSnap) => {
            if (docSnap.exists() && docSnap.data().events) {
                const eventsMap = docSnap.data().events;
                
                // Convert the map back into an array for your React components
                const eventsArray = Object.keys(eventsMap).map(key => ({
                    id: key,
                    ...eventsMap[key]
                }));
                
                // Sort by startTime (since we can't use Firestore's orderBy on a single document)
                eventsArray.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
                setSchedule(eventsArray);
            } else {
                setSchedule([]);
            }
        });
        
        return unsubscribe;
    }, [isVisible]);

    // 3. Settings Listeners
    useEffect(() => {
        if (!isVisible) return; 

        const unsubs = [];

        // 1. The single master listener for all configuration data (Public)
        unsubs.push(
            onSnapshot(doc(db, "system", "config"), (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setConditionsText(data.general?.conditions || "");
                    setTeams(data.teams?.list || []);
                    setStandings(data.standings?.data || []);
                    setHomeContent(data.home || {});
                    setDonationsContent(data.donations || {});
                    setGlobalStyle(data.style || null);
                    setBrackets(data.brackets || { main: {}, consolation: {} });
                    setAllLineups(data.lineups || {});
                    setAliasesCsv(data.aliases?.csv || "");
                    
                    if (data.navigation?.items) {
                        const dbItems = data.navigation.items;
                        const mergedItems = [...dbItems];
                        DEFAULT_NAV.forEach(defItem => {
                            if (!dbItems.find(i => i.link === defItem.link)) mergedItems.push(defItem);
                        });
                        setNavigation(mergedItems);
                    } else {
                        setNavigation(DEFAULT_NAV);
                    }
                    setNavReady(true);
                } else {
                    setNavReady(true);
                }
            })
        );

        // 2. The custom pages listener (Still a collection)
        unsubs.push(
            onSnapshot(collection(db, "pages"), snap => setCustomPages(snap.docs.map(d => ({id: d.id, ...d.data()}))))
        );

        // 3. The Roles listener (PRIVATE - Only attach if Staff!)
        // This prevents public users from getting "Permission Denied" console errors
        if (userRole && (userRole.role === 'admin' || userRole.role === 'captain')) {
            unsubs.push(
                onSnapshot(doc(db, "system", "roles"), (snap) => {
                    if (snap.exists()) {
                        const data = snap.data();
                        const usersArray = Object.keys(data).map(uid => ({ uid: uid, ...data[uid] }));
                        setAllUsers(usersArray);
                    } else {
                        setAllUsers([]);
                    }
                })
            );
        }

        return () => unsubs.forEach(u => u());
    }, [isVisible, userRole]);

    // --- Actions ---
    const login = (e, p) => signInWithEmailAndPassword(auth, e, p);
    
    // UPDATED REGISTER: Now accepts 'role' and creates a user_roles document
    const register = async (email, password, role = 'admin') => {
        // 1. Create the user FIRST so Firebase gives them a token to read the database
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        
        // 2. NOW check the hard limit (They are temporarily authorized to read it)
        const rolesDoc = await getDoc(doc(db, "system", "roles"));
        if (rolesDoc.exists() && Object.keys(rolesDoc.data()).length >= 50) {
            
            // 3. ROLLBACK: Delete the account we just created
            await deleteUser(cred.user);
            throw new Error("Registration closed: Maximum of 50 authorized users reached. Please reach out to the organizers to increase this limit");
        }

        // 4. SUCCESS: Save them to the master roles document
        await setDoc(doc(db, "system", "roles"), {
            [cred.user.uid]: {
                email: email,
                role: role,
                teamId: "" 
            }
        }, { merge: true }); // setDoc with merge is safe here because the UID is a top-level key!
        
        return cred;
    };
    
    const logout = () => signOut(auth);

    const updateUserRoleData = async (uid, data) => {
        const updatePayload = {};
        for (const key in data) {
            updatePayload[`${uid}.${key}`] = data[key];
        }
        await updateDoc(doc(db, "system", "roles"), updatePayload);
    };

    const saveSetting = (settingKey, data) => {
        return setDoc(doc(db, "system", "config"), { 
            [settingKey]: data 
        }, { merge: true });
    };

    // NEW: Update User Data (For Admin to assign teams)
    // const updateUserRoleData = async (uid, data) => {
    //     await updateDoc(doc(db, "user_roles", uid), data);
    // };

    const addEvent = async (data) => {
        const newId = doc(collection(db, "dummy")).id; 
        // CHANGED: Using updateDoc so it correctly nests the new match inside the events object
        await updateDoc(doc(db, "system", "schedule"), {
            [`events.${newId}`]: data
        });
    };

    const updateEvent = async (id, data) => {
        // To safely merge updates into a nested object, we use dot notation for every changed field
        const updatePayload = {};
        for (const key in data) {
            updatePayload[`events.${id}.${key}`] = data[key];
        }
        await updateDoc(doc(db, "system", "schedule"), updatePayload);
    };

    const deleteEvent = async (id) => {
        // Tell Firestore to delete this specific match key from the events object
        await updateDoc(doc(db, "system", "schedule"), {
            [`events.${id}`]: deleteField()
        });
    };

    const toggleCommentary = async (matchId, userEmail) => {
        const matchData = schedule.find(m => m.id === matchId);
        const currentSignups = matchData.commentators || [];
        let updated;

        if (currentSignups.includes(userEmail)) {
            updated = currentSignups.filter(email => email !== userEmail);
        } else if (currentSignups.length < 2) {
            updated = [...currentSignups, userEmail];
        } else {
            throw new Error("This match already has 2 commentators.");
        }

        // Update just the commentators array for this specific match
        await updateDoc(doc(db, "system", "schedule"), { 
            [`events.${matchId}.commentators`]: updated 
        });
    };

    // const saveSetting = (docName, data) => setDoc(doc(db, "settings", docName), data, { merge: true });
    const updateConditions = (t) => saveSetting("general", { conditions: t });
    const saveTeams = (l) => saveSetting("teams", { list: l });
    const saveStandings = (d) => saveSetting("standings", { data: d });
    const saveBrackets = (d) => saveSetting("brackets", d);
    const saveHomeContent = (d) => saveSetting("home", d);
    const saveDonationsContent = (d) => saveSetting("donations", d);
    const saveNavigation = (items) => saveSetting("navigation", { items });
    const saveGlobalStyle = (d) => saveSetting("style", d);
    const saveAliasesCsv = (csv) => saveSetting("aliases", { csv });

    const addCustomPage = (data) => addDoc(collection(db, "pages"), data);
    const updateCustomPage = (id, data) => updateDoc(doc(db, "pages", id), data);
    const deleteCustomPage = (id) => deleteDoc(doc(db, "pages", id));

    const saveMatchLineup = async (matchId, teamId, players) => {
        // CHANGED: Using updateDoc so Firestore respects the nested dot-notation
        await updateDoc(doc(db, "system", "config"), { 
            [`lineups.${matchId}.${teamId}`]: players, 
            [`lineups.${matchId}.lastUpdated`]: new Date() 
        });
    };
    

    const value = {
        user, userRole, allUsers, schedule, conditionsText, teams, standings, homeContent, navigation, donationsContent, customPages, globalStyle, loading, brackets, allLineups,
        login, register, logout, addEvent, updateEvent, deleteEvent, 
        updateConditions, saveTeams, saveStandings, saveHomeContent, saveNavigation, saveDonationsContent, saveAliasesCsv,
        addCustomPage, updateCustomPage, deleteCustomPage, saveGlobalStyle, updateUserRoleData,
        saveBrackets, saveMatchLineup, toggleCommentary
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
}